import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSortModule, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { signalStore } from '@ngrx/signals';
import { from, map, Observable, of } from 'rxjs';
import { UserService } from 'shared/domain';
import { InputDirective, NoResultsComponent, PageEvent, PaginatorComponent } from 'shared/ui-common';
import { AddZToUTCPipe, NamePipe, ServerResponse, withPagedList, IndexedDbService } from 'shared/util-common';

import { Guideline, GuidelineOrigin } from '@treetop/shared/model';

export enum OfflineTableColumnName {
  Title = 'title',
  Version = 'version',
  Owner = 'owner',
  Publisher = 'publisher',
  PublishedAt = 'publishedAt',
}

export type SortableField = 'title' | 'publishedAt';
export type SortOrder = 'asc' | 'desc';

export interface GuidelineSort {
  field: SortableField;
  order: SortOrder;
}

export const OfflineGuidelinesTableStore = signalStore(withPagedList());

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-offline-guidelines-table',
  templateUrl: './offline-guidelines-table.component.html',
  styleUrls: ['./offline-guidelines-table.component.scss'],
  imports: [
    InputDirective,
    FormsModule,
    TranslocoPipe,
    AddZToUTCPipe,
    MatCell,
    MatCellDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    NamePipe,
    MatColumnDef,
    MatHeaderCellDef,
    PaginatorComponent,
    MatSortModule,
    NoResultsComponent,
    MatNoDataRow,
    TranslocoDatePipe,
  ],
  providers: [OfflineGuidelinesTableStore],
})
export class OfflineGuidelinesTableComponent implements OnInit {
  protected offlineGuidelinesTableStore = inject(OfflineGuidelinesTableStore);

  private indexedDbService = inject(IndexedDbService);
  private router = inject(Router);
  private userService = inject(UserService);

  protected vm = this.offlineGuidelinesTableStore.viewModel;

  protected dataSource = computed(() => this.vm().data);

  protected searchTerm = signal('');
  protected guidelinesSort = signal<GuidelineSort | undefined>({
    field: 'publishedAt',
    order: 'desc',
  });

  protected displayedColumns: string[] = [
    OfflineTableColumnName.Title,
    OfflineTableColumnName.Version,
    OfflineTableColumnName.Owner,
    OfflineTableColumnName.Publisher,
    OfflineTableColumnName.PublishedAt,
  ];

  public ngOnInit() {
    this.offlineGuidelinesTableStore.init({
      serverCall: (page, pageSize) => this.getGuidelines(page, pageSize),
    });
  }

  private getGuidelines(page: number, pageSize: number): Observable<ServerResponse<Guideline>> {
    const currentUser = this.userService.user();

    if (!currentUser?.id) {
      return of(this.createEmptyResponse(page, pageSize));
    }

    return from(
      this.indexedDbService.getGuidelinesByUserId(
        currentUser.id,
        this.searchTerm(),
        page,
        pageSize,
        this.guidelinesSort()?.field,
        this.guidelinesSort()?.order,
      ),
    ).pipe(
      map(({ list, total }) => ({
        list,
        page,
        itemsPerPage: pageSize,
        maxElements: total,
        maxPages: Math.ceil(total / pageSize),
      })),
    );
  }

  private createEmptyResponse(page: number, pageSize: number): ServerResponse<Guideline> {
    return {
      list: [],
      page,
      itemsPerPage: pageSize,
      maxElements: 0,
      maxPages: 0,
    };
  }

  protected onOpenGuideline(uniqueId: string) {
    this.router.navigate(['offline', 'guideline', uniqueId]);
  }

  protected handlePageEvent(pageEvent: PageEvent) {
    this.offlineGuidelinesTableStore.loadPage(pageEvent.page, pageEvent.itemsPerPage);
  }

  protected handleSort(sort: Sort) {
    const field = this.getSortableField(sort.active);
    const order = this.getSortOrder(sort.direction);

    if (order === null) {
      this.guidelinesSort.set(undefined);
    } else {
      this.guidelinesSort.set({ field, order });
    }

    this.offlineGuidelinesTableStore.loadPage();
  }

  private getSortableField(active: string): SortableField {
    return active === OfflineTableColumnName.Title ? 'title' : 'publishedAt';
  }

  private getSortOrder(direction: string): SortOrder | null {
    if (direction === 'asc') {
      return 'asc';
    }

    if (direction === 'desc') {
      return 'desc';
    }

    return null;
  }

  protected handleClearFilter() {
    this.searchTerm.set('');
    this.offlineGuidelinesTableStore.loadPage();
  }

  protected readonly GuidelineOrigin = GuidelineOrigin;
  protected readonly OfflineTableColumnName = OfflineTableColumnName;
}
